import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
    Trophy,
    ChevronDown,
    ChevronUp,
    Loader2,
    Target,
} from 'lucide-react';
import { cn, formatScore } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { BetScoreBreakdown } from '@/components/bets/BetScoreBreakdown';
import { useAuth } from '@/context/AuthContext';
import { RACE_LEADERBOARD } from '@/graphql/queries';
import type { RaceLeaderboardQuery } from '@/gql/graphql';

type LeaderboardItem = NonNullable<
    NonNullable<RaceLeaderboardQuery['raceLeaderboard']>['items']
>[number];

type BetItem = NonNullable<LeaderboardItem['bet']>;

// --- Bet Drill-down (grouped by gender) ---

function BetDetail({ bet }: { bet: BetItem }) {
    const { t } = useTranslation('bets');

    const rankings = bet.rankings ?? [];
    const bonuses = bet.bonuses ?? [];
    const genders = [...new Set(rankings.map(r => r.gender).filter(Boolean))] as string[];

    return (
        <div className="space-y-3">
            {genders.map((gender) => {
                const genderRankings = rankings.filter(r => r.gender === gender);
                const genderBonuses = bonuses.filter(b => b.gender === gender || b.gender == null);
                const genderLabel = gender === 'MALE' ? t('leaderboard.betMale') : t('leaderboard.betFemale');

                return (
                    <div key={gender}>
                        <div className="flex items-center justify-between mb-1">
                            <span className={cn(
                                "px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide",
                                gender === 'MALE' ? "bg-blue-50 text-blue-700" : "bg-pink-50 text-pink-700"
                            )}>
                                {genderLabel}
                            </span>
                        </div>
                        <BetScoreBreakdown
                            rankings={genderRankings}
                            bonuses={gender === genders[genders.length - 1] ? genderBonuses : bonuses.filter(b => b.gender === gender)}
                        />
                    </div>
                );
            })}
        </div>
    );
}

// --- Leaderboard Entry ---

function LeaderboardEntry({
    item,
    rank,
    isCurrentUser,
}: {
    item: LeaderboardItem;
    rank: number;
    isCurrentUser: boolean;
}) {
    const { t } = useTranslation('bets');
    const [expanded, setExpanded] = useState(false);
    const bet = item.bet;

    return (
        <div className={cn(
            "rounded-2xl border transition-all",
            isCurrentUser
                ? "bg-emerald-50/50 border-emerald-200 ring-1 ring-emerald-100"
                : "bg-white border-slate-100"
        )}>
            {/* Header row */}
            <button
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                className="w-full flex items-center gap-3 p-4 text-left"
            >
                {/* Avatar + rank badge */}
                <div className="relative flex-shrink-0">
                    <UserAvatar
                        src={item.user?.pictureUrl}
                        alt={item.user?.username ?? ''}
                        size="sm"
                    />
                    <div className={cn(
                        "absolute -bottom-1 -right-1 w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center border-2 border-white",
                        getMedalStyle(rank)
                    )}>
                        {rank}
                    </div>
                </div>

                {/* Username */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={cn(
                            "font-bold truncate",
                            isCurrentUser ? "text-emerald-900" : "text-slate-900"
                        )}>
                            {item.user?.username ?? '?'}
                        </span>
                        {isCurrentUser && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 flex-shrink-0">
                                {t('leaderboard.you')}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {bet?.rankings && [...new Set(bet.rankings.map(r => r.gender).filter(Boolean))].map(g =>
                            `${g === 'MALE' ? '♂' : '♀'} ${bet.rankings?.filter(r => r.gender === g).length ?? 0}`
                        ).join(' · ')}
                    </p>
                </div>

                {/* Total score */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn(
                        "text-xl font-black",
                        isCurrentUser ? "text-emerald-600" : "text-slate-800"
                    )}>
                        {formatScore(item.totalScore ?? 0)}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">
                        {t('leaderboard.pts')}
                    </span>
                    {expanded
                        ? <ChevronUp size={16} className="text-slate-300" />
                        : <ChevronDown size={16} className="text-slate-300" />
                    }
                </div>
            </button>

            {/* Expanded drill-down */}
            {expanded && bet && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    <BetDetail bet={bet} />
                </div>
            )}
        </div>
    );
}

// --- Main Page ---

export function LeaderboardPage() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const { user } = useAuth();

    const [page, setPage] = useState(1);

    const { data, loading, error, fetchMore } = useQuery(RACE_LEADERBOARD, {
        variables: { raceId: id!, pagination: { page: 1, limit: 20 } },
        skip: !id,
    });

    const leaderboard = data?.raceLeaderboard;
    const items = leaderboard?.items ?? [];
    const hasNextPage = leaderboard?.hasNextPage ?? false;
    const showPodium = items.length >= 3;

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        await fetchMore({
            variables: { pagination: { page: nextPage, limit: 20 } },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult?.raceLeaderboard) return prev;
                return {
                    raceLeaderboard: {
                        ...fetchMoreResult.raceLeaderboard,
                        items: [
                            ...(prev.raceLeaderboard?.items ?? []),
                            ...(fetchMoreResult.raceLeaderboard.items ?? []),
                        ],
                    },
                };
            },
        });
        setPage(nextPage);
    };

    if (!id) return null;

    return (
        <Page>
            <PageHeader
                title={t('leaderboard.title')}
                backLink={`/races/${id}`}
                width="standard"
            />

            <PageContent width="standard" className="space-y-3">
                {/* Loading */}
                {loading && (
                    <div className="flex items-center justify-center py-20 text-slate-400">
                        <Loader2 size={28} className="animate-spin text-emerald-600" />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <ErrorBanner message={tc('error.generic')} />
                )}

                {/* Empty */}
                {!loading && !error && items.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                        <Target size={36} className="mb-3 opacity-20" />
                        <p className="text-sm font-medium">{t('leaderboard.empty')}</p>
                    </div>
                )}

                {/* Leaderboard list */}
                {items.length > 0 && (
                    <div className="space-y-2">
                        {/* Top 3 podium */}
                        {showPodium && (
                            <div className="flex items-end justify-center gap-3 py-4">
                                {/* 2nd place */}
                                <div className="flex flex-col items-center gap-1">
                                    <div className="relative">
                                        <UserAvatar
                                            src={items[1].user?.pictureUrl}
                                            alt={items[1].user?.username ?? ''}
                                            size="sm"
                                            className="border-2 border-slate-300 ring-2 ring-slate-200"
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-300 text-white text-[10px] font-black flex items-center justify-center border-2 border-white">
                                            2
                                        </div>
                                    </div>
                                    <span className="text-xs font-bold text-slate-500 truncate max-w-[80px]">
                                        {items[1].user?.username}
                                    </span>
                                    <span className="text-sm font-black text-slate-600">
                                        {formatScore(items[1].totalScore ?? 0)}
                                    </span>
                                </div>

                                {/* 1st place */}
                                <div className="flex flex-col items-center gap-1 -mt-4">
                                    <Trophy size={20} className="text-amber-400 mb-1" />
                                    <div className="relative">
                                        <UserAvatar
                                            src={items[0].user?.pictureUrl}
                                            alt={items[0].user?.username ?? ''}
                                            size="md"
                                            className="border-2 border-amber-400 ring-2 ring-amber-200 shadow-lg shadow-amber-200"
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 text-white text-xs font-black flex items-center justify-center border-2 border-white">
                                            1
                                        </div>
                                    </div>
                                    <span className="text-xs font-bold text-slate-700 truncate max-w-[80px]">
                                        {items[0].user?.username}
                                    </span>
                                    <span className="text-lg font-black text-amber-600">
                                        {formatScore(items[0].totalScore ?? 0)}
                                    </span>
                                </div>

                                {/* 3rd place */}
                                <div className="flex flex-col items-center gap-1">
                                    <div className="relative">
                                        <UserAvatar
                                            src={items[2].user?.pictureUrl}
                                            alt={items[2].user?.username ?? ''}
                                            size="sm"
                                            className="border-2 border-amber-600 ring-2 ring-amber-100"
                                        />
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-black flex items-center justify-center border-2 border-white">
                                            3
                                        </div>
                                    </div>
                                    <span className="text-xs font-bold text-slate-500 truncate max-w-[80px]">
                                        {items[2].user?.username}
                                    </span>
                                    <span className="text-sm font-black text-slate-600">
                                        {formatScore(items[2].totalScore ?? 0)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Full list (all entries, including podium — expandable for details) */}
                        {items.map((item, index) => {
                            const rank = index + 1;
                            return (
                                <LeaderboardEntry
                                    key={item.user?.id ?? index}
                                    item={item}
                                    rank={rank}
                                    isCurrentUser={item.user?.id === user?.id}
                                />
                            );
                        })}
                    </div>
                )}

                {/* Load more */}
                {hasNextPage && (
                    <div className="flex justify-center pt-2 pb-4">
                        <button
                            onClick={handleLoadMore}
                            className="bg-white border border-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold text-sm hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-2 shadow-sm"
                        >
                            {t('leaderboard.loadMore')}
                        </button>
                    </div>
                )}
            </PageContent>
        </Page>
    );
}
